import {
  booleanAttribute,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  Injector,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import {
  ConnectedPosition,
  createFlexibleConnectedPositionStrategy,
  createNoopScrollStrategy,
  createOverlayRef,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { isTapPointer, TAP_SLOP_PX } from '@shared-util-dom';
import { getRandomId } from '../../utils/get-random-id';
import { KeyNamesEnum } from '../../constants/key-names.constants';
import { UiTooltipPanelComponent } from './ui-tooltip-panel.component';

const SHOW_DELAY_MS = 400;
const HIDE_DELAY_MS = 100;
/** Within this long of one tooltip closing, the next opens without its delay (moving along a row). */
const GROUP_WINDOW_MS = 300;
const OFFSET_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

const POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -OFFSET_PX },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: OFFSET_PX },
  { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: OFFSET_PX },
  { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -OFFSET_PX },
];

/** Shared by every tooltip on the page, for the group delay. */
let lastHiddenAt = 0;
let openCount = 0;

/**
 * The kit's tooltip: opens on hover after a delay, on keyboard focus at once, and on a touch or pen
 * tap; stays while the pointer is over it, so its text can be selected and copied; closes on
 * `Escape`, blur, a press outside, scrolling, or the wheel over it — never on a timer (WCAG
 * 1.4.13). `matTooltip` is set up app-wide as non-interactive and mouse-only
 * (`provideTooltipDefaults`), which is why this is not built on it.
 *
 * With `uiTooltipAnnounce` off, the panel is hidden from screen readers — for text the trigger
 * already holds in full, such as a truncated table cell.
 *
 * `<span [fr8botUiTooltip]="fullName" [uiTooltipDisabled]="!truncated()">…</span>`
 *
 * With `uiTooltipIfTruncated`, it opens only while the host's own text is cut off with "…" (the
 * host is the clipped box) and, without a text of its own, shows the host's text on one line. The
 * check runs on each opening, from the event that opens it.
 *
 * `<div class="tw:truncate" fr8botUiTooltip uiTooltipIfTruncated [uiTooltipAnnounce]="false">…</div>`
 */
@Directive({
  selector: '[fr8botUiTooltip]',
  exportAs: 'fr8botUiTooltip',
  host: {
    '[attr.aria-describedby]': 'describedBy()',
    '(mouseenter)': 'scheduleShow()',
    '(mouseleave)': 'scheduleHide()',
    '(focus)': 'showOnKeyboardFocus()',
    '(blur)': 'hideOnBlur()',
    '(pointerdown)': 'startTap($event)',
    '(click)': 'toggleOnTap($event)',
  },
})
export class UiTooltipDirective {
  readonly fr8botUiTooltip = input('');
  readonly uiTooltipDisabled = input(false);
  readonly uiTooltipAnnounce = input(true);
  readonly uiTooltipIfTruncated = input(false, { transform: booleanAttribute });

  readonly isOpen = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly injector = inject(Injector);
  private readonly panelId = `ui-tooltip-${getRandomId()}`;

  private overlayRef: OverlayRef | null = null;
  private panelRef: ComponentRef<UiTooltipPanelComponent> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private focusFrame: number | null = null;
  private tapStart: { x: number; y: number } | null = null;
  /** The focus that follows a press is the pointer's, not the keyboard's. */
  private pressing = false;
  private pointerInPanel = false;
  private removeListeners: (() => void)[] = [];

  protected readonly describedBy = signal<string | null>(null);

  constructor() {
    effect(() => {
      const text = this.fr8botUiTooltip();
      const announce = this.uiTooltipAnnounce();

      if (this.uiTooltipDisabled() || (!text && !this.uiTooltipIfTruncated())) {
        untracked(() => this.hide());
        return;
      }

      this.panelRef?.setInput('text', text || this.hostText());
      this.panelRef?.setInput('announce', announce);
      untracked(() => this.describedBy.set(this.isOpen() && announce ? this.panelId : null));
    });

    inject(DestroyRef).onDestroy(() => {
      this.hide();
      this.overlayRef?.dispose();
    });
  }

  show(): void {
    this.clearTimer();

    if (this.isOpen() || this.uiTooltipDisabled()) {
      return;
    }

    if (this.uiTooltipIfTruncated() && this.host.scrollWidth <= this.host.clientWidth) {
      return;
    }

    const text = this.fr8botUiTooltip() || (this.uiTooltipIfTruncated() ? this.hostText() : '');

    if (!text) {
      return;
    }

    const overlayRef = this.overlayRef ?? this.createOverlay();
    const panelRef = overlayRef.attach(new ComponentPortal(UiTooltipPanelComponent));
    panelRef.setInput('text', text);
    panelRef.setInput('panelId', this.panelId);
    panelRef.setInput('announce', this.uiTooltipAnnounce());
    panelRef.changeDetectorRef.detectChanges();
    this.panelRef = panelRef;

    this.listenWhileOpen(panelRef.location.nativeElement);
    this.isOpen.set(true);
    openCount++;
    this.describedBy.set(this.uiTooltipAnnounce() ? this.panelId : null);
  }

  hide(): void {
    this.clearTimer();

    if (!this.isOpen()) {
      return;
    }

    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];
    this.overlayRef?.detach();
    this.panelRef = null;
    this.pointerInPanel = false;
    this.isOpen.set(false);
    this.describedBy.set(null);
    openCount--;
    lastHiddenAt = Date.now();
  }

  protected scheduleShow(): void {
    if (this.isOpen()) {
      this.clearTimer();
      return;
    }

    // Moving straight from one trigger to the next enters it before the previous one has closed.
    const grouped = openCount > 0 || Date.now() - lastHiddenAt < GROUP_WINDOW_MS;
    const delay = grouped ? 0 : SHOW_DELAY_MS;
    this.schedule(() => this.show(), delay);
  }

  protected scheduleHide(): void {
    this.schedule(() => this.hide(), HIDE_DELAY_MS);
  }

  protected showOnKeyboardFocus(): void {
    if (this.pressing) {
      return;
    }

    // Focus scrolls a trigger that is out of view into it, and that scroll would close a tooltip
    // opened right now; scroll events of a frame fire before its animation frame callbacks.
    this.clearTimer();
    this.focusFrame = requestAnimationFrame(() => {
      this.focusFrame = null;
      this.show();
    });
  }

  /** Pressing inside the tooltip to select its text moves focus off the trigger. */
  protected hideOnBlur(): void {
    if (!this.pointerInPanel) {
      this.hide();
    }
  }

  protected startTap(event: PointerEvent): void {
    this.tapStart = isTapPointer(event.pointerType) ? { x: event.clientX, y: event.clientY } : null;
    // The browser focuses the trigger after this press, within the same task.
    this.pressing = true;
    setTimeout(() => (this.pressing = false));
  }

  protected toggleOnTap(event: MouseEvent): void {
    const start = this.tapStart;
    this.tapStart = null;

    if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_SLOP_PX) {
      return;
    }

    if (this.isOpen()) {
      this.hide();
    } else {
      this.show();
    }
  }

  private hostText(): string {
    return (this.host.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private createOverlay(): OverlayRef {
    this.overlayRef = createOverlayRef(this.injector, {
      positionStrategy: createFlexibleConnectedPositionStrategy(this.injector, this.host)
        .withPositions(POSITIONS)
        .withPush(true)
        .withViewportMargin(VIEWPORT_MARGIN_PX),
      // Closed by the scroll listener below instead: the CDK close strategy only watches
      // registered scroll containers, and the table bodies aren't `cdkScrollable`.
      scrollStrategy: createNoopScrollStrategy(),
    });

    return this.overlayRef;
  }

  private listenWhileOpen(panel: HTMLElement): void {
    const document = this.host.ownerDocument;
    const enterPanel = (): void => {
      this.pointerInPanel = true;
      this.clearTimer();
    };
    const leavePanel = (): void => {
      this.pointerInPanel = false;
      this.scheduleHide();
    };
    const hideNow = (): void => this.hide();
    const onKeydown = (event: KeyboardEvent): void => {
      if (event.key === KeyNamesEnum.Escape) {
        // Caught on the way down, so a dialog around the trigger doesn't close with it.
        event.stopPropagation();
        this.hide();
      }
    };
    const onOutsidePress = (event: Event): void => {
      const target = event.target as Node | null;
      if (target && !this.host.contains(target) && !panel.contains(target)) {
        this.hide();
      }
    };
    const onScroll = (event: Event): void => {
      if (!(event.target instanceof Node) || !panel.contains(event.target)) {
        this.hide();
      }
    };

    panel.addEventListener('mouseenter', enterPanel);
    panel.addEventListener('mouseleave', leavePanel);
    // An overlay is outside the scroll area under it: the wheel over it would scroll nothing.
    panel.addEventListener('wheel', hideNow, { passive: true });
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('pointerdown', onOutsidePress, true);
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    this.removeListeners = [
      () => panel.removeEventListener('mouseenter', enterPanel),
      () => panel.removeEventListener('mouseleave', leavePanel),
      () => panel.removeEventListener('wheel', hideNow),
      () => document.removeEventListener('keydown', onKeydown, true),
      () => document.removeEventListener('pointerdown', onOutsidePress, true),
      () => document.removeEventListener('scroll', onScroll, true),
    ];
  }

  private schedule(action: () => void, delay: number): void {
    this.clearTimer();
    this.timer = setTimeout(action, delay);
  }

  private clearTimer(): void {
    if (this.focusFrame !== null) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
